import React, { useEffect, useRef, useState } from 'react';
import { CustomizationsState, ViewerCustomizationInterface, ViewerGroupCustomizationInterface, ViewerOptionInterface } from '../../core';

import CarouselContainer from 'nuka-carousel';
import { ArrowLeftSolid } from './../../icons';
import { ArrowRightSolid } from './../../icons';
import { ArrowsAltSolid } from './../../icons';
import { PlusCircleSolid } from './../../icons';
import { SearchMinusSolid } from './../../icons';
import { SearchPlusSolid } from './../../icons';

import styles from './MenuChoiceCustomizations.module.scss';
import { Item, ProductArea, TemplateArea, useZakeke, ZakekeDesigner, ImageItem, TextItem } from 'zakeke-configurator-react';
import ItemImage, { EditImageItem } from './ItemImage';
import ItemText, { EditTextItem } from './ItemText';
import { ArrowLeft, ArrowLeftIconStyled, ArrowRight, ArrowRightIconStyled, Button, Icon } from './Atomic';
import Select, { GroupBase, OptionProps, SingleValueProps } from 'react-select';
import { useTranslation } from 'react-i18next';

export interface MenuChoiceCustomizationsProps {
  customizations: ViewerGroupCustomizationInterface;
  customizationsState: CustomizationsState;
  showCustomizationInfo: (info: ViewerCustomizationInterface) => void;
  setCustomizationsState: (customizationsState: CustomizationsState) => void;
  optionSelect: (option: ViewerOptionInterface) => void;
  onAddImageItem: (guid: number, areaId: number) => Promise<void>;
  onItemRemove: (guid: string) => Promise<void>;
}

const MenuChoiceCustomizations: React.FC<MenuChoiceCustomizationsProps> = (props) => {
  const {
    setItemImageFromFile,
    setItemText,
    setItemItalic,
    setItemBold,
    setItemColor,
    setItemFontFamily,
    setItemTextOnPath,
    createImage,
    setCamera,
    items,
    currentTemplate,
    getTemplateUploadRestrictictions,
  } = useZakeke();

  const [t] = useTranslation();

  const customizerRef = useRef<any | null>(null);
  const filteredAreas = props.customizations.productAreas;
  const isMobile = window.innerWidth <= 1024;
  let finalVisibleAreas: ProductArea[] = [];

  function getSupportedUploadFileFormats(templateId: number, areaId: number) {
    const restrictions = getTemplateUploadRestrictictions(templateId, areaId);
    const fileFormats: string[] = [];

    if (restrictions.isJpgAllowed) fileFormats.push('.jpg', '.jpeg');
    if (restrictions.isPngAllowed) fileFormats.push('.png');
    if (restrictions.isSvgAllowed) fileFormats.push('.svg');
    if (restrictions.isEpsAllowed) fileFormats.push('.eps');
    if (restrictions.isPdfAllowed) fileFormats.push('.pdf');

    return fileFormats;
  }

  let translatedAreas = filteredAreas.map((area) => {
    return { id: area.id, name: area.name };
  });

  const isItemEditable = (item: Item, templateArea?: TemplateArea) => {
    if (!item.constraints) return false;

    let { canEdit, canMove, canRotate, canResize, canDelete, canChangeFontColor, canChangeFontFamily, canChangeFontWeight, isPrintable } =
      item.constraints;

    if (!isPrintable) return false;

    let common = canEdit || canMove || canRotate || canResize || canDelete;
    let text = canChangeFontColor || canChangeFontFamily || canChangeFontWeight;
    let image = canEdit || (templateArea && (templateArea.uploadRestrictions.isUserImageAllowed || !templateArea.disableSellerImages));

    if (item.type === 0) return common || text;
    else return common || image;
  };

  const [actualAreaId, setActualAreaId] = useState<number>(finalVisibleAreas && finalVisibleAreas.length > 0 ? finalVisibleAreas[0].id : 0);

  filteredAreas.length > 0 &&
    filteredAreas.forEach((filteredArea) => {
      let currentTemplateArea = currentTemplate!.areas.find((x) => x.id === filteredArea.id);
      let itemsOfTheArea = items.filter((item) => item.areaId === filteredArea.id);
      const areAllItemsStatic = !itemsOfTheArea.some((item) => {
        return !item.constraints || item.constraints.canMove || item.constraints.canRotate || item.constraints.canResize || item.constraints.canEdit;
      });
      if (!areAllItemsStatic || !currentTemplateArea || currentTemplateArea?.canAddImage || currentTemplateArea?.canAddText)
        finalVisibleAreas.push(filteredArea);
    });

  let currentTemplateArea = currentTemplate!.areas.find((x) => x.id === actualAreaId);
  const showAddTextButton = !currentTemplateArea || currentTemplateArea.canAddText;
  const showUploadButton = !currentTemplateArea || (currentTemplateArea.canAddImage && currentTemplateArea.uploadRestrictions.isUserImageAllowed);
  const supportedFileFormats = getSupportedUploadFileFormats(props.customizations.currentTemplate!.id, props.customizationsState.actualAreaId).join(
    ', '
  );
  let itemsFiltered = items.filter((item) => item.areaId === actualAreaId);

  const allStaticElements = !itemsFiltered.some((item) => {
    return !item.constraints || item.constraints.canMove || item.constraints.canRotate || item.constraints.canResize;
  });

  useEffect(() => {
    if (finalVisibleAreas.length > 0 && actualAreaId === 0) setActualAreaId(finalVisibleAreas[0].id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalVisibleAreas]);

  const handleAddTextClick = () => {
    props.setCustomizationsState({ ...props.customizationsState, showAddTextDialog: true });
  };

  const handleItemRemoved = async (guid: string) => {
    await props.onItemRemove(guid);
  };

  const showGalleryButton = !currentTemplateArea || (currentTemplateArea.canAddImage && !currentTemplateArea.disableSellerImages);

  const handleItemImageChange = async (item: EditImageItem, file: File) => {
    try {
      props.setCustomizationsState({ ...props.customizationsState, isLoading: true });
      await setItemImageFromFile(item.guid, file);
    } catch (ex) {
      console.error(ex);
    } finally {
      props.setCustomizationsState({ ...props.customizationsState, isLoading: false });
    }
  };

  const handleItemImageGallery = async (item: EditImageItem) => {
    props.setCustomizationsState({
      ...props.customizationsState,
      imageGalery: { ...props.customizationsState.imageGalery, showAddImageDialog: true, currentAction: 'EDIT', selectedItem: item },
    });
  };

  const handleItemPropChange = (item: EditTextItem | EditImageItem, prop: string, value: string | boolean | File) => {
    switch (prop) {
      case 'remove':
        handleItemRemoved(item.guid);
        break;
      case 'image-upload':
        handleItemImageChange(item as EditImageItem, value as File);
        break;
      case 'image-gallery':
        handleItemImageGallery(item as EditImageItem);
        break;
      case 'text':
        setItemText(item.guid, value as string);
        break;
      case 'font-italic':
        setItemItalic(item.guid, value as boolean);
        break;
      case 'font-bold':
        setItemBold(item.guid, value as boolean);
        break;
      case 'font-color':
        setItemColor(item.guid, value as string);
        break;
      case 'font-family':
        setItemFontFamily(item.guid, value as string);
        break;
      case 'text-path':
        setItemTextOnPath(item.guid, actualAreaId, value as boolean);
        setTimeout(() => props.setCustomizationsState({ ...props.customizationsState, forceUpdate: !props.customizationsState.forceUpdate }), 100);
        break;
    }
  };

  const SelectOption = (props: JSX.IntrinsicAttributes & OptionProps<any, boolean, GroupBase<any>>) => {
    return (
      <div className={styles.ff_OptionContainer} {...props}>
        <span>{props.data.name}</span>
      </div>
    );
  };

  const SelectSingleValue = (props: JSX.IntrinsicAttributes & SingleValueProps<any, boolean, GroupBase<any>>) => {
    return (
      <div className={styles.ff_SingleValueContainer} {...props}>
        <span>{props.data.name}</span>
      </div>
    );
  };

  const handleUploadImageClick = async (
    addItemImage: (guid: any, imageId: number) => Promise<void>,
    createImage: (file: File, progress?: (percentage: number) => void) => Promise<any>
  ) => {
    if (currentTemplate && actualAreaId) {
      const fileFormats = getSupportedUploadFileFormats(currentTemplate.id, actualAreaId);
      let input = document.createElement('input');
      input.setAttribute('accept', fileFormats.join(','));
      input.setAttribute('type', 'file');
      input.addEventListener('change', async (e) => {
        const files = (e.currentTarget as HTMLInputElement).files;
        if (files && files.length > 0 && actualAreaId) {
          props.setCustomizationsState({ ...props.customizationsState, isLoading: true });
          try {
            const image = await createImage(files[0], (progress: number) => console.log(progress));
            addItemImage(image.imageID, actualAreaId);
            input.remove();
          } catch (ex) {
            props.setCustomizationsState({ ...props.customizationsState, showErrorDialog: true });
            console.error(ex);
          } finally {
            props.setCustomizationsState({ ...props.customizationsState, isLoading: false });
          }
        }
      });
      document.body.appendChild(input);
      input.click();
    }
  };

  const setSelectedCarouselSlide = (index: number) => {
    props.setCustomizationsState({ ...props.customizationsState, selectedCarouselSlide: index });
  };

  const setMoveElements = (moveElements: boolean) => {
    props.setCustomizationsState({ ...props.customizationsState, moveElements: moveElements });
  };

  const handleAddImageFromGalleryClick = async () => {
    props.setCustomizationsState({
      ...props.customizationsState,
      imageGalery: { ...props.customizationsState.imageGalery, showAddImageDialog: true, currentAction: 'ADD' },
    });
  };

  useEffect(() => {
    const areaId = actualAreaId;
    if (areaId) {
      props.setCustomizationsState({
        ...props.customizationsState,
        actualAreaId: areaId,
      });
      const area = filteredAreas.find((x) => x.id === areaId);
      if (area && area.cameraLocationID) {
        setCamera(area.cameraLocationID);
      }
    }
  }, [actualAreaId]);

  return (
    <>
      {!props.customizationsState.moveElements && (
        <div className={styles.ff_DesignerContainer} data-testid='menu-choice-customization'>
          {!isMobile && (
            <CarouselContainer
              slidesToScroll={1}
              speed={50}
              slidesToShow={props.customizationsState.slidesToShow}
              renderBottomCenterControls={() => <span />}
              slideIndex={props.customizationsState.selectedCarouselSlide}
              afterSlide={setSelectedCarouselSlide}
              style={{ borderBottom: '2px solid rgb(196, 196, 196)' }}
              renderCenterRightControls={() => {
                if (
                  props.customizationsState.selectedCarouselSlide !==
                  (finalVisibleAreas.length - props.customizationsState.slidesToShow > 0
                    ? finalVisibleAreas.length - props.customizationsState.slidesToShow
                    : props.customizationsState.selectedCarouselSlide)
                )
                  return (
                    <ArrowRight onClick={() => setSelectedCarouselSlide(props.customizationsState.selectedCarouselSlide + 1)}>
                      <ArrowRightIconStyled>
                        <ArrowRightSolid />
                      </ArrowRightIconStyled>
                    </ArrowRight>
                  );
              }}
              renderCenterLeftControls={() => {
                if (props.customizationsState.selectedCarouselSlide !== 0)
                  return (
                    <ArrowLeft onClick={() => setSelectedCarouselSlide(props.customizationsState.selectedCarouselSlide - 1)}>
                      <ArrowLeftIconStyled>
                        <ArrowLeftSolid />
                      </ArrowLeftIconStyled>
                    </ArrowLeft>
                  );
              }}
            >
              {finalVisibleAreas.map((area) => (
                <div
                  className={`${styles.area} ${actualAreaId === area.id ? styles.selected : ''}`}
                  key={area.id}
                  onClick={() => setActualAreaId(area.id)}
                >
                  {t(area.name, area.name)}
                </div>
              ))}
            </CarouselContainer>
          )}

          {isMobile && translatedAreas.length > 1 && (
            <div className={styles.ff_selectContainer}>
              <span>{t('CUSTOMIZATION_CUSTOMIZABLEAREAS', 'Customizable Areas')}</span>
              <Select
                styles={{
                  container: (base) => ({
                    ...base,
                    minWidth: 300,
                  }),
                }}
                isSearchable={false}
                options={translatedAreas}
                menuPosition='fixed'
                components={{
                  Option: SelectOption,
                  SingleValue: SelectSingleValue,
                }}
                value={[translatedAreas!.find((x) => x.id === actualAreaId)]}
                onChange={(area: any) => setActualAreaId(area.id)}
              />
            </div>
          )}

          {itemsFiltered.length === 0 && !(showAddTextButton || showUploadButton) && <span>{'No customizable items'}</span>}

          {itemsFiltered.map((item) => {
            if (item.type === 0 && isItemEditable(item, currentTemplateArea))
              return <ItemText key={item.guid} handleItemPropChange={handleItemPropChange} item={item as TextItem} />;
            else if (item.type === 1 && isItemEditable(item, currentTemplateArea))
              return (
                <ItemImage
                  uploadImgDisabled={false}
                  key={item.guid}
                  handleItemPropChange={handleItemPropChange}
                  item={item as ImageItem}
                  currentTemplateArea={currentTemplateArea!}
                />
              );

            return null;
          })}
          {(showAddTextButton || showUploadButton || showGalleryButton) && (
            <div className={styles.ff_UploadButtons}>
              {showAddTextButton && (
                <Button disabled={props.customizationsState.isUploadDisabled} isFullWidth onClick={handleAddTextClick}>
                  <Icon>
                    <PlusCircleSolid />
                  </Icon>
                  <span>{t('CUSTOMIZATION_ADDTEXT', 'Add text')}</span>
                </Button>
              )}

              {showUploadButton && (
                <>
                  <Button
                    disabled={props.customizationsState.isUploadDisabled}
                    isFullWidth
                    onClick={() => handleUploadImageClick(props.onAddImageItem, createImage)}
                  >
                    <Icon>
                      <PlusCircleSolid />
                    </Icon>
                    <span>
                      <span>
                        {itemsFiltered.some((item) => item instanceof ImageItem && isItemEditable(item, currentTemplateArea))
                          ? t('CUSTOMIZATION_UPLOAD_ANOTHERIMAGE', 'Upload another image')
                          : t('CUSTOMIZATION_UPLOADIMAGES', 'Upload Images')}{' '}
                      </span>
                    </span>
                  </Button>
                </>
              )}

              {showGalleryButton && (
                <Button isFullWidth onClick={handleAddImageFromGalleryClick}>
                  <Icon>
                    <PlusCircleSolid />
                  </Icon>
                  <span>{t('CUSTOMIZATION_ADD_CLIPAPRT', 'Add clipart')}</span>
                </Button>
              )}
              <span className={styles.ff_menu_choice_customizations_supportformat}>
                {t('CUSTOMIZATION_SUPPORT_FILEFORMATS', 'Supported file formats:') + ' ' + supportedFileFormats}
              </span>
            </div>
          )}

          {itemsFiltered.length > 0 && !allStaticElements && (
            <Button onClick={() => setMoveElements(true)}>
              <Icon>
                <ArrowsAltSolid />
              </Icon>
              <span>{t('CUSTOMIZATION_MOVEELEMENTS', 'Move elements')} </span>
            </Button>
          )}
        </div>
      )}
      {props.customizationsState.moveElements && (
        <div className={styles.ff_ZakekeDesignerContainer}>
          <ZakekeDesigner ref={customizerRef} areaId={actualAreaId} />
          <div className={styles.ff_IconsAndDesignerContainer}>
            <div className={styles.ff_ZoomIconIn} onClick={() => customizerRef.current.zoomIn()}>
              <SearchPlusSolid />
            </div>
            <div className={styles.ff_ZoomIconOut} onClick={() => customizerRef.current.zoomOut()}>
              <SearchMinusSolid />
            </div>
          </div>
          <Button isFullWidth onClick={() => setMoveElements(false)}>
            <span>{t('CUSTOMIZATION_OK', 'OK')} </span>
          </Button>
        </div>
      )}
      {/*props.customizations.map((customization) => {
        return (
          <div key={customization.id} className={styles.ff_menu_choice_customization}>
            <MenuChoiceCustomization
              customization={customization}
              saveText={(text) => props.saveText(customization, text)}
              saveFont={(font) => props.saveFont(customization, font)}
              saveBold={(bold) => props.saveBold(customization, bold)}
              saveItalic={(italic) => props.saveItalic(customization, italic)}
              optionSelect={props.optionSelect}
            />

            <MenuChoiceCustomizationPreview customization={customization} showCustomizationInfo={props.showCustomizationInfo} />
          </div>
        );
      })*/}
    </>
  );
};

export default MenuChoiceCustomizations;
